import { Box, Grid } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { TableTreatmentSuggestionCounter } from "./charts/TableTreatmentSuggestionCounter";
import { PieChartColorBy } from "./charts/PieChartColorBy";
import { MiniMap } from "./charts/MiniMap";
import { ChartWrapper } from "./ChartWrapper";
import { t } from "i18next";
import { TableDistancePerAttributeValue } from "./charts/TableDistancePerAttributeValue";
import { PieChartFullScore } from "./charts/PieChartFullScore";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardTypes } from "../../../../project/models/Dashboard";
import { useStores } from "../../../../shared/stores/RootStore";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useCallback } from "react";

const DashboardViewBase: React.FC = () => {
  const { mapStore, projectStore, dashboardStore } = useStores();
  const selectedProject = projectStore.getSelected();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = parseInt(active.id as string, 10);
        const newIndex = parseInt(over.id as string, 10);
        const reorderedItems = arrayMove(
          dashboardStore.dashboardItems,
          oldIndex,
          newIndex,
        );
        dashboardStore.reorderDashboardItems(reorderedItems);
      }
    },
    [dashboardStore],
  );

  return (
    <Box position="relative">
      <DashboardHeader />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={dashboardStore.dashboardItems.map((_, i) => i.toString())}
          strategy={rectSortingStrategy}
        >
          <Grid p={4} pt={0} templateColumns="repeat(6, 1fr)" gap={4}>
            {dashboardStore.dashboardItems.map((item, i) => {
              var content: React.ReactNode = null;
              var defaultColSpan = 3;

              switch (item.type) {
                case DashboardTypes.ItemType.PieChartColorBy: {
                  const typedItem = item as DashboardTypes.PieChartColorBy;
                  content = (
                    <PieChartColorBy
                      value={typedItem.colorBy}
                      onChange={(colorBy) => {
                        const updatedItem: DashboardTypes.PieChartColorBy = {
                          ...typedItem,
                          colorBy: colorBy,
                        };
                        dashboardStore.updateDashboardItem(i, updatedItem);
                      }}
                      projectPermissions={selectedProject?.permissions}
                    />
                  );
                  break;
                }

                case DashboardTypes.ItemType.PieChartFullScore: {
                  defaultColSpan = 6;
                  content = <PieChartFullScore />;
                  break;
                }

                case DashboardTypes.ItemType.MiniMap: {
                  defaultColSpan = 6;
                  content = <MiniMap />;
                  break;
                }

                case DashboardTypes.ItemType.TableTreatmentSuggestionCounter: {
                  content = <TableTreatmentSuggestionCounter />;
                  break;
                }

                case DashboardTypes.ItemType.TableDistancePerAttributeValue: {
                  defaultColSpan = 6;
                  content = <TableDistancePerAttributeValue />;
                  break;
                }
              }

              return (
                <ChartWrapper
                  key={i}
                  id={i.toString()}
                  name={item.name}
                  projectPermissions={selectedProject?.permissions}
                  defaultName={t(`map:dashboard.${item.type}.title`)}
                  colSpan={item.colSpan || defaultColSpan}
                  onResize={(colSpan) => {
                    dashboardStore.updateDashboardItem(i, { ...item, colSpan });

                    if (item.type === DashboardTypes.ItemType.MiniMap) {
                      mapStore.refreshMapWindow();
                    }
                  }}
                  onRename={(name) => {
                    dashboardStore.updateDashboardItem(i, {
                      ...item,
                      name,
                    });
                  }}
                  onDelete={() => dashboardStore.deleteDashboardItem(i)}
                >
                  {content}
                </ChartWrapper>
              );
            })}
          </Grid>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export const DashboardView = observer(DashboardViewBase);
